import { DeleteOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { deleteBusinessContact } from 'lib/api/contacts';
import { Contact, ContactWithBusiness } from 'lib/api/contacts/types';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import React from 'react';
import { useDispatch } from 'react-redux';

type Props = {
  open: boolean;
  contact: Contact | ContactWithBusiness | null;
  onClose: () => void;
};

const DeleteContactDialog: React.FC<Props> = ({ open, contact, onClose }) => {
  if (!contact) return null;
  const dispatch = useDispatch();
  const { mutate: deleteFn, isPending } = deleteBusinessContact(dispatch);
  const handleConfirmDelete = () => {
    if (!contact) return;
    deleteFn(contact.id, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const profiles: string[] = [];
  if (contact.isLegalRepresentative) profiles.push('Representante legal');
  if (contact.isPartner) profiles.push('Socio');
  if (contact.selected) profiles.push('Contacto principal');
  if (contact.invoicing) profiles.push('Contacto de facturación');

  const hasProfiles = profiles.length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        onClose={onClose}
        title="Eliminar contacto"
        icon={<DeleteOutlined color="error" />}
        color="error"
      />
      <DialogContent>
        <Typography variant="body1">
          ¿Estás seguro que deseas eliminar al contacto{' '}
          <strong>{contact.name}</strong>?
        </Typography>

        {hasProfiles && (
          <Box mt={2}>
            <Alert severity="warning">
              <Typography variant="body2" gutterBottom>
                Este contacto tiene los siguientes perfiles asignados:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                {profiles.map((profile) => (
                  <li key={profile}>
                    <Typography variant="body2" component="span">
                      {profile}
                    </Typography>
                  </li>
                ))}
              </Box>
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1, opacity: 0.8 }}
              >
                Al eliminarlo, estos perfiles se perderán y deberás reasignarlos
                a otro contacto si es necesario.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="error"
          onClick={handleConfirmDelete}
          loading={isPending}
          disabled={isPending}
        >
          Eliminar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteContactDialog;
